import api from './api';

class CSRFService {
  constructor() {
    this.token = null;
    this.tokenPromise = null;
  }

  async getToken() {
    // Return cached token if available
    if (this.token) {
      return this.token;
    }

    // Return existing promise if token is being fetched
    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    // Fetch new token
    this.tokenPromise = this.fetchToken();
    this.token = await this.tokenPromise;
    this.tokenPromise = null;

    return this.token;
  }

  async fetchToken() {
    try {
      const response = await api.get('/csrf-token');
      return response.data.csrfToken;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);

      // If it's an auth error, we might not be logged in
      if (error.response?.status === 401) {
        console.error('Not authenticated - cannot fetch CSRF token');
      }

      return null;
    }
  }

  clearToken() {
    this.token = null;
    this.tokenPromise = null;
  }

  async refreshToken() {
    this.clearToken();
    return this.getToken();
  }
}

export const csrfService = new CSRFService();

// Interceptor to add CSRF token to requests
export const setupCSRFInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use(
    async (config) => {
      // Add CSRF token to POST, PUT, DELETE requests
      if (['post', 'put', 'delete'].includes(config.method.toLowerCase())) {
        const token = await csrfService.getToken();
        if (token) {
          config.headers['X-CSRF-Token'] = token;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Handle CSRF token errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 403 &&
          error.response?.data?.error?.includes('CSRF')) {
        // Clear token and retry once
        csrfService.clearToken();
        const originalRequest = error.config;

        if (!originalRequest._csrfRetry) {
          originalRequest._csrfRetry = true;
          const newToken = await csrfService.getToken();
          if (newToken) {
            originalRequest.headers['X-CSRF-Token'] = newToken;
            return axiosInstance(originalRequest);
          }
        }
      }

      return Promise.reject(error);
    }
  );
};